/**
 * PATHS AND ROUTES — THE PURE HALF OF NAVIGATION.
 *
 * Split from `router.ts` so it can be proved without a browser. That is not
 * tidiness for its own sake: the rules below are the product's, they are the
 * ones a deep link depends on, and a rule that can only be checked by driving a
 * real browser is a rule that will be checked rarely. Everything here is a
 * function of a string.
 *
 * ONE ACTIVE CONVERSATION, ENFORCED BY SHAPE. There is no route whose
 * destination names a conversation and no third segment that could carry one
 * into a conversation read. The Main Chat is singular, so the URL grammar
 * cannot express a second thread — which is a stronger guarantee than a
 * convention that the Main Chat view happens to ignore ids.
 *
 * THE FIVE AREAS DID NOT BREAK THE OLD ADDRESSES. Every pre-area path is
 * translated here, with its record id intact, so a notification, bookmark or
 * search result minted before the redesign still opens the record it named.
 * The translation is deliberately in this pure module rather than in the App:
 * it is a rule about URLs, and it can be proved without a DOM.
 */

import { DEFAULT_DESTINATION, destinationById } from './destinations';

export interface Route {
	/** The destination id — always one of `DESTINATIONS`. */
	destination: string;
	/** A tab or a record section inside the destination, e.g. `observatory`,
	 *  `search`, `window`. */
	section: string | null;
	/** A canonical record id, e.g. `run:73f32753`. Only meaningful WITH a
	 *  section, and never read by the Main Chat. */
	recordId: string | null;
}

function decodeSegment(raw: string): string {
	try {
		return decodeURIComponent(raw).trim();
	} catch {
		// A MALFORMED ESCAPE IS NOT A ROUTE. Returning the raw text is the
		// honest fallback: it will fail to resolve to a record and the view
		// will say so, rather than throwing inside the router.
		return raw.trim();
	}
}

/**
 * THE OLD NAME FOR A PLACE, TRANSLATED TO THE NEW ONE.
 *
 * Each branch preserves the record id, because that is the part a deep link is
 * FOR. A branch that dropped it would turn "open this finding" into "open the
 * findings screen", which is the failure the whole URL grammar exists to avoid.
 */
function fromLegacyDestination(
	destination: string,
	section: string | null,
	recordId: string | null,
): Route | null {
	switch (destination) {
		case 'home':
			// THE HOME DASHBOARD IS THE WORKSPACE'S OVERVIEW TAB. It used to
			// translate to the Main Chat, which opened the wrong screen for every
			// old bookmark: Home answers "is Scout working", and the chat answers
			// a question. The tab label is the new name; the address is the one
			// link the Owner already had.
			return { destination: 'workspace', section: 'overview', recordId: null };
		case 'research':
			// `/research` was Research Control and is still Research Control.
			// A window deep link keeps its section and id.
			return { destination: 'research', section, recordId };
		case 'observatory':
			// `/observatory/episode/<id>` → `/research/observatory/<id>`.
			return {
				destination: 'research',
				section: 'observatory',
				recordId: section === 'episode' ? recordId : null,
			};
		case 'experiments':
			if (section === 'experiment' && recordId !== null) {
				return { destination: 'research', section: 'experiments', recordId };
			}
			if (section === 'receipt' && recordId !== null) {
				// THE RECEIPT IS A DIFFERENT SURFACE INSIDE THE SAME TAB. Its
				// address carries the marker rather than a fourth path segment,
				// so the grammar stays three segments deep.
				return {
					destination: 'research',
					section: 'experiments',
					recordId: `receipt:${recordId}`,
				};
			}
			return { destination: 'research', section: 'experiments', recordId: null };
		case 'evidence':
			// `record` and `adjudication` were two sections of one reader. The
			// id itself says which (adjudications are `adj:`), and the view
			// reads that prefix rather than a second path segment.
			return {
				destination: 'discoveries',
				section: 'evidence',
				recordId: section === 'record' || section === 'adjudication' ? recordId : null,
			};
		case 'knowledge':
			return { destination: 'discoveries', section: 'knowledge', recordId: null };
		case 'improvements':
			if (section === 'charter') {
				return { destination: 'workspace', section: 'charter', recordId: null };
			}
			if (section === 'decisions' || section === 'decision') {
				return { destination: 'workspace', section: 'decisions', recordId };
			}
			// `proposal` and `critique` both sat inside the proposals panel; the
			// new address names the tab and lets the id's prefix select the row.
			return {
				destination: 'workspace',
				section: 'improvements',
				recordId: section === 'proposal' || section === 'critique' ? recordId : null,
			};
		case 'history':
			return {
				destination: 'history',
				section: section === 'conversation' ? 'conversations' : null,
				recordId: section === 'conversation' ? recordId : null,
			};
		case 'search': {
			// The old grammar was `/search/<group>/<query>`. The group is a
			// filter, not a record, and is not preserved; the QUERY is, because
			// that is the question the link asked.
			const query = section !== null && isSearchGroup(section) ? recordId : section;
			return { destination: 'history', section: 'search', recordId: query };
		}
		case 'notifications':
			return { destination: 'workspace', section: 'notifications', recordId: null };
		case 'resources':
			return { destination: 'workspace', section: 'resources', recordId: null };
		case 'status':
			return { destination: 'workspace', section: 'status', recordId: null };
		default:
			return null;
	}
}

const SEARCH_GROUPS = ['everything', 'conversation', 'research', 'science', 'governance'];

function isSearchGroup(value: string): boolean {
	return SEARCH_GROUPS.includes(value);
}

export function parsePath(pathname: string): Route {
	const segments = pathname
		.split('/')
		.map(decodeSegment)
		.filter((segment) => segment.length > 0);
	const [first, section, recordId] = segments;
	if (first === undefined) {
		return { destination: DEFAULT_DESTINATION, section: null, recordId: null };
	}
	if (destinationById(first) !== undefined) {
		// THE TWO AREAS THAT MOVED OUT OF RESEARCH. Their old addresses are
		// deeper than the new ones, so the translation preserves the record id in
		// the place the new route reads it: a workspace detail is `/spaces/<id>`
		// (a section, because the area has no tabs), and a capability detail is
		// `/foundry/capability/<id>`.
		if (first === 'research' && section === 'spaces') {
			return {
				destination: 'spaces',
				section: recordId === undefined ? null : recordId,
				recordId: null,
			};
		}
		if (first === 'research' && section === 'foundry') {
			return {
				destination: 'foundry',
				section: recordId === undefined ? null : recordId,
				recordId: null,
			};
		}
		// A PRE-AREA IMPROVEMENTS ADDRESS INSIDE THE SYSTEM AREA. The old
		// `/workspace` was Improvements itself; it is now the System overview, so
		// an old bookmark to the bare address must keep landing on Home.
		return {
			destination: first,
			section: section ?? null,
			recordId: recordId ?? null,
		};
	}
	const legacy = fromLegacyDestination(first, section ?? null, recordId ?? null);
	if (legacy !== null) return legacy;
	return { destination: DEFAULT_DESTINATION, section: null, recordId: null };
}

export function pathFor(route: Route): string {
	const segments = [route.destination];
	if (route.section !== null) segments.push(route.section);
	if (route.recordId !== null && route.section !== null) segments.push(route.recordId);
	return `/${segments.map((segment) => encodeURIComponent(segment)).join('/')}`;
}

/** The path a destination's own link points at. */
export function pathForDestination(id: string): string {
	return pathFor({ destination: id, section: null, recordId: null });
}

/**
 * THE PATH FOR A TAB. The area's own address is its first tab's address, so
 * `/chat`, `/history` and `/workspace` do not carry a redundant segment.
 */
export function pathForTab(destinationId: string, section: string | null): string {
	return pathFor({ destination: destinationId, section, recordId: null });
}
